import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Copy, Download } from "lucide-react";
import api from "@/lib/api";
import { StatusChip, cad, day, downloadCsv, errMsg, shortId } from "@/components/sponsored/format";

const PAGE = 25;
const FILTERS = [["", "All"], ["PENDING", "Pending"], ["AVAILABLE", "Available"], ["REVERSED", "Reversed"]];
const HEAD = ["Reward ID", "Your order", "Cyannect sale ID", "Shopper ref", "Sale", "Rate", "Cyannect", "You funded", "Status", "Available"];

function Id({ value, testid }) {
  if (!value) return <span>—</span>;
  const copy = () => navigator.clipboard?.writeText(value).then(() => toast.success("Copied"), () => {});
  return (
    <span className="inline-flex items-center gap-1 max-w-[170px]" title={value}>
      <span className="truncate">{shortId(value, 12)}</span>
      <button type="button" onClick={copy} aria-label={`Copy ${value}`} data-testid={testid}
        className="text-slate-400 hover:text-slate-700 transition-colors shrink-0"><Copy className="w-3 h-3" /></button>
    </span>
  );
}

function Row({ r }) {
  return (
    <tr className="border-t border-slate-100" data-testid={`sc-tx-row-${r.sponsored_reward_id}`}>
      <td className="py-2 pr-3 font-mono text-xs"><Id value={r.sponsored_reward_id} testid={`sc-tx-copy-reward-${r.sponsored_reward_id}`} /></td>
      <td className="py-2 pr-3 font-mono text-xs"><Id value={r.merchant_order_id} testid={`sc-tx-copy-order-${r.sponsored_reward_id}`} /></td>
      <td className="py-2 pr-3 font-mono text-[11px] text-slate-500"><Id value={r.cyannect_sale_id} testid={`sc-tx-copy-sale-${r.sponsored_reward_id}`} /></td>
      <td className="py-2 pr-3 font-mono text-xs whitespace-nowrap">{r.shopper_ref}</td>
      <td className="py-2 pr-3 tabular-nums whitespace-nowrap">{cad(r.sale_cad)}</td>
      <td className="py-2 pr-3 whitespace-nowrap">{r.total_rate_pct}%</td>
      <td className="py-2 pr-3 tabular-nums whitespace-nowrap">{cad(r.cyannect_cashback_cad)}</td>
      <td className="py-2 pr-3 tabular-nums font-semibold text-[#004C3F] whitespace-nowrap">{cad(r.merchant_sponsored_cad)}</td>
      <td className="py-2 pr-3"><StatusChip status={r.state} /></td>
      <td className="py-2 pr-3 text-xs text-slate-500 whitespace-nowrap">{r.state === "REVERSED" ? day(r.reversed_at) : day(r.available_at)}</td>
    </tr>
  );
}

function Body({ data, error, retry }) {
  if (error) {
    return (
      <div className="flex items-center justify-between gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700" data-testid="sc-tx-error">
        <span>{error}</span>
        <button onClick={retry} className="pf-btn-glass px-3 py-1 rounded-full text-xs font-semibold" data-testid="sc-tx-retry">Try again</button>
      </div>
    );
  }
  if (!data) return <p className="text-sm text-slate-500" data-testid="sc-tx-loading">Loading…</p>;
  if (data.rows.length === 0) {
    return <p className="text-sm text-slate-500 italic" data-testid="sc-tx-empty">No verified repeat purchases yet. They appear here after a returning shopper's Verified Sale.</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="text-left text-[10px] uppercase tracking-widest text-slate-500"><tr>{HEAD.map((h) => <th key={h} scope="col" className="pb-2 pr-3 whitespace-nowrap">{h}</th>)}</tr></thead>
        <tbody>{data.rows.map((r) => <Row key={r.sponsored_reward_id} r={r} />)}</tbody>
      </table>
    </div>
  );
}

export function TransactionsTable() {
  const [q, setQ] = useState("");
  const [state, setState] = useState("");
  const [skip, setSkip] = useState(0);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [csvBusy, setCsvBusy] = useState(false);
  const load = useCallback(() => {
    setError(null);
    api.get("/merchant/sponsored-cashback/transactions", { params: { q, state, limit: PAGE, skip } })
      .then((r) => setData(r.data)).catch((e) => setError(errMsg(e, "We couldn't load your repeat purchases. Please try again.")));
  }, [q, state, skip]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const csv = () => {
    setCsvBusy(true);
    downloadCsv("/merchant/sponsored-cashback/transactions.csv", { q, state }, "sponsored-cashback-transactions.csv")
      .catch((e) => toast.error(errMsg(e, "Couldn't download the CSV")))
      .finally(() => setCsvBusy(false));
  };
  return (
    <div className="space-y-4" data-testid="sc-transactions">
      <div className="flex flex-wrap items-center gap-2">
        <input className="pf-input flex-1 min-w-[200px] px-3 py-2 text-sm" placeholder="Search order, reward ID, sale ID or shopper ref" value={q}
          aria-label="Search repeat purchases" onChange={(e) => { setQ(e.target.value); setSkip(0); }} data-testid="sc-tx-search" />
        {FILTERS.map(([v, l]) => (
          <button key={l} onClick={() => { setState(v); setSkip(0); }} data-testid={`sc-tx-filter-${l.toLowerCase()}`} aria-pressed={state === v}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold ${state === v ? "pf-btn-primary" : "pf-btn-glass"}`}>{l}</button>
        ))}
        <button onClick={csv} disabled={csvBusy} className="pf-btn-glass px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1 disabled:opacity-50" data-testid="sc-tx-csv-btn">
          <Download className="w-3.5 h-3.5" /> {csvBusy ? "Preparing…" : "CSV"}
        </button>
      </div>
      <Body data={data} error={error} retry={load} />
      {data && !error && data.total > PAGE && (
        <div className="flex items-center justify-end gap-2 text-xs text-slate-500">
          <span data-testid="sc-tx-range">{skip + 1}–{Math.min(skip + PAGE, data.total)} of {data.total}</span>
          <button disabled={skip === 0} onClick={() => setSkip(skip - PAGE)} className="pf-btn-glass px-3 py-1 rounded-full disabled:opacity-40" data-testid="sc-tx-prev">Previous</button>
          <button disabled={skip + PAGE >= data.total} onClick={() => setSkip(skip + PAGE)} className="pf-btn-glass px-3 py-1 rounded-full disabled:opacity-40" data-testid="sc-tx-next">Next</button>
        </div>
      )}
    </div>
  );
}
